import {
  type BinaryData,
  useDocumentReferenceForBinaryId,
  useGetBinaryDocument,
} from '@ctw/shared/api/fhir/binaries';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { useGetPatientByUpid } from '@ctw/shared/api/fhir/patient';
import { Button } from '@ctw/shared/components/button';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Heading } from '@ctw/shared/components/heading';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { VerticalDivider } from '@ctw/shared/components/vertical-divider';
import { classifyCdaDocumentError } from '@ctw/shared/content/document-viewer/_internal/renderers/cda-document-error';
import {
  CdaSectionSelectionProvider,
  useCdaSectionSelectionContext,
} from '@ctw/shared/content/document-viewer/_internal/renderers/context/cda-section-selection-provider';
import { DocumentViewerProvider } from '@ctw/shared/content/document-viewer/_internal/renderers/context/document-viewer-provider';
import { CdaSearchInput } from '@ctw/shared/content/document-viewer/_internal/viewer/cda-search-input';
import { DocumentViewerControlButton } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-button';
import { DocumentViewerControlLink } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-link';
import { DocumentViewerControlMenu } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-control-menu';
import { DocumentViewerDetailFields } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-detail-fields';
import { DocumentViewerDetailSubsection } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-detail-subsection';
import { DocumentViewerHeaderFields } from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-header-fields';
import {
  type DocumentViewerTab,
  DocumentViewerTabMenu,
  DocumentViewerTabs,
} from '@ctw/shared/content/document-viewer/_internal/viewer/document-viewer-tab-menu';
import { DocumentRenderer } from '@ctw/shared/content/document-viewer/document-renderer';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useBreakpointQuery, useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { useInterval } from '@ctw/shared/hooks/use-interval';
import {
  DOCUMENT_VIEWER_CONTROL_BUTTON_BACK_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_CLOSE_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_PDF_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_OPEN_IN_NEW_TAB_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_BUTTON_PRINT_TEST_ID,
  DOCUMENT_VIEWER_CONTROL_MENU_DOWNLOAD_TEST_ID,
  DOCUMENT_VIEWER_SELECTION_CONTEXT_BAR_TEST_ID,
} from '@ctw/shared/test/ids';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { downloadBlob, printBinaryDocument, printBlobUrl } from '@ctw/shared/utils/documents';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { prettifyXmlString } from '@ctw/shared/utils/xml';
import {
  faArrowUpRightFromSquare,
  faFileDownload,
  faPrint,
} from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { compact, orderBy } from 'lodash-es';
import { type MouseEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface ExportPdfArgs {
  exportAction: 'download' | 'print';
  cdaDocumentSectionIdAllowlist?: Array<string>;
}

export interface DocumentViewerControls {
  close: boolean;
  openInNewTab: boolean;
  print: boolean;
  download: boolean;
  search: boolean;
  deepLinkSections: boolean;
}

interface BaseDocumentViewerProps {
  onClose: () => void;
  controls: DocumentViewerControls;
}

interface DocumentViewerPropsForDocumentReference extends BaseDocumentViewerProps {
  documentReference: DocumentModel;
  binaryData?: never;
  binaryId?: never;
}

interface DocumentViewerPropsForBinaryId extends BaseDocumentViewerProps {
  documentReference?: never;
  binaryData?: never;
  binaryId: string;
}

interface DocumentViewerPropsForBinaryData extends BaseDocumentViewerProps {
  documentReference?: DocumentModel;
  binaryData: BinaryData;
  binaryId?: never;
}

type DocumentViewerProps =
  | DocumentViewerPropsForDocumentReference
  | DocumentViewerPropsForBinaryId
  | DocumentViewerPropsForBinaryData;

export const DocumentViewer = memo(
  ({
    documentReference: externalDocumentReference,
    binaryId: externalBinaryId,
    binaryData: externalBinaryData,
    onClose,
    controls,
  }: DocumentViewerProps) => {
    const { service } = useZuiContext();
    const telemetry = useTelemetry();
    const patientContext = usePatientContextIfAvailable();

    const headerContainerRef = useRef<HTMLDivElement>(null);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [cdaDocumentXml, setCdaDocumentXml] = useState<string | null>(null);
    const [prettifiedCdaDocumentXml, setPrettifiedCdaDocumentXml] = useState<string | null>(null);
    const [cdaDocumentPdfPreviewUrl, setCdaDocumentPdfPreviewUrl] = useState<string | null>(null);
    const buildingCdaPdfPreviewRef = useRef(false);
    const [cdaDocument, setCdaDocument] = useState<CdaDocument | null>(null);
    const [cdaParseError, setCdaParseError] = useState<Error | null>(null);
    const [controlMenuIsLoading, setControlMenuIsLoading] = useState(false);
    const buildingCdaDocumentRef = useRef(false);
    const { requestContext } = useCTW();

    const { breakpoints: contentBreakpoints, width: contentContainerWidth } = useContainerQuery({
      containerRef: contentContainerRef,
    });
    const { breakpoints: screenBreakpoints } = useBreakpointQuery();

    /*
     * Note: This is implemented as an interval check instead of using `useScrollSpy` threaded down
     * to the document content scroll container because such threading results in re-renders which
     * cause instability in the document content nodes which affect browser Ctrl+F behavior (APP-4333)
     *
     */
    const [collapsedHeader, setCollapsedHeader] = useState<boolean>(false);
    useInterval({
      intervalInMilliseconds: 20,
      intervalFunction: () => {
        setCollapsedHeader(contentRef.current ? contentRef.current.scrollTop > 35 : false);
      },
    });

    const patientQuery = useGetPatientByUpid({
      upid: externalDocumentReference?.patientUPID ?? '',
      enabled: !patientContext,
    });
    const patient = useMemo(
      () => (patientContext ? patientContext.patient : patientQuery.data),
      [patientContext, patientQuery.data],
    );

    const binaryId = useMemo(
      () => externalBinaryId ?? externalDocumentReference?.binaryId,
      [externalBinaryId, externalDocumentReference?.binaryId],
    );
    const binaryQuery = useGetBinaryDocument(
      binaryId ?? externalDocumentReference?.binaryId ?? '',
      externalBinaryData === undefined && binaryId !== undefined,
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: Need to include externalDocumentReference
    const binaryData = useMemo(
      () => externalBinaryData ?? binaryQuery.data,
      [externalDocumentReference?.id, externalBinaryData?.binary, binaryQuery.data],
    );

    const documentReferenceQuery = useDocumentReferenceForBinaryId({
      upid: patient?.UPID ?? '',
      binaryId: binaryId ?? '',
      enabled:
        externalDocumentReference === undefined && binaryId !== undefined && Boolean(patient?.UPID),
      includePlainText: true,
    });
    const documentReference = useMemo(
      () => externalDocumentReference ?? documentReferenceQuery.data,
      [externalDocumentReference, documentReferenceQuery.data],
    );
    const isPlainTextDocument = useMemo(
      () => binaryData?.binary?.contentType === 'text/plain',
      [binaryData?.binary?.contentType],
    );
    const isXmlDocument = useMemo(
      () => documentReference?.contentType?.includes('xml'),
      [documentReference?.contentType],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally over-specific deps
    const isLoading = useMemo(
      () =>
        binaryQuery.isLoading ||
        patientQuery.isLoading ||
        documentReferenceQuery.isLoading ||
        (isXmlDocument && !cdaDocument && !cdaParseError && buildingCdaDocumentRef.current),
      [
        binaryQuery.isLoading,
        patientQuery.isLoading,
        documentReferenceQuery.isLoading,
        isXmlDocument,
        cdaDocument?.id,
        cdaParseError,
      ],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally over-specific deps
    const boundaryContext = useMemo(
      () => ({
        documentPatientUpid: patient?.UPID,
        documentReferenceId: documentReference?.id,
        documentBinaryId: binaryData?.binary?.id,
        documentContentType: binaryData?.binary.contentType,
      }),
      [patient?.UPID, documentReference?.id, binaryData?.binary?.id],
    );

    const tabs: Array<DocumentViewerTab> = useMemo(
      (): Array<DocumentViewerTab> =>
        (
          [
            {
              id: 'document',
              label: 'Document',
              onlyVisibleForXmlDocuments: false,
            },
            {
              id: 'details',
              label: 'Details',
              onlyVisibleForXmlDocuments: false,
            },
            {
              id: 'xml',
              label: 'XML',
              onlyVisibleForXmlDocuments: true,
            },
            ...(cdaDocument && requestContext.authTokenState.userType === 'zus'
              ? ([
                  {
                    id: 'pdf_preview',
                    label: 'PDF Preview',
                    onlyVisibleForXmlDocuments: true,
                  },
                ] satisfies Array<DocumentViewerTab>)
              : []),
          ] satisfies Array<DocumentViewerTab>
        ).filter((tab) => isXmlDocument || tab.onlyVisibleForXmlDocuments === false),
      [requestContext.authTokenState.userType, cdaDocument, isXmlDocument],
    );

    const [selectedTab, internalSetSelectedTab] = useState<DocumentViewerTab>(tabs[0]);

    const setSelectedTab = useCallback(
      (tab: DocumentViewerTab) => {
        internalSetSelectedTab(tab);

        if (tab.id === 'xml' && cdaDocumentXml !== null && prettifiedCdaDocumentXml === null) {
          setPrettifiedCdaDocumentXml(prettifyXmlString(cdaDocumentXml));
        }
      },
      [cdaDocumentXml, prettifiedCdaDocumentXml],
    );

    const downloadRaw = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (binaryData) {
          downloadBlob(binaryData.binary.blob, `document-${binaryId}`);
        }

        setControlMenuIsLoading(false);
      },
      [binaryData, binaryId],
    );

    const printRaw = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (binaryData) {
          printBinaryDocument(binaryData.binary.blob, binaryData.binary.contentType);
        }

        setControlMenuIsLoading(false);
      },
      [binaryData],
    );

    const generateServerPdfPreview = useCallback(() => {
      if (buildingCdaPdfPreviewRef.current !== true) {
        buildingCdaPdfPreviewRef.current = true;

        const effectiveDocumentReference = externalDocumentReference ?? documentReference;
        if (patient?.id !== undefined && effectiveDocumentReference?.id !== undefined) {
          void fetch(`${getBffApiBaseUrl(requestContext.env)}/v1/cda/pdf`, {
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            method: 'POST',
            body: JSON.stringify({
              patientId: patient.id,
              documentReferenceId: effectiveDocumentReference.id,
            }),
          })
            .then(async (response) => {
              if (!response.ok) {
                notify({
                  type: 'error',
                  title: 'Unable to download PDF',
                  body: 'An error occurred while generating a PDF for download. Please try again.',
                });
              } else {
                setCdaDocumentPdfPreviewUrl(URL.createObjectURL(await response.blob()));
              }
            })
            .finally(() => {
              buildingCdaPdfPreviewRef.current = false;
            });
        }
      }
    }, [
      externalDocumentReference,
      documentReference,
      patient?.id,
      requestContext.authToken,
      requestContext.env,
    ]);

    useEffect(() => {
      if (selectedTab.id === 'pdf_preview' && cdaDocumentPdfPreviewUrl === null) {
        generateServerPdfPreview();
      }
    }, [selectedTab, cdaDocumentPdfPreviewUrl, generateServerPdfPreview]);

    const exportPdf = useCallback(
      ({ exportAction, cdaDocumentSectionIdAllowlist }: ExportPdfArgs) => {
        const effectiveDocumentReference = externalDocumentReference ?? documentReference;

        const performExportAction = (pdfUrl: string) => {
          if (exportAction === 'print') {
            printBlobUrl(pdfUrl);
          } else {
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style = 'display: none';
            a.href = pdfUrl;
            a.download = `${cdaDocumentSectionIdAllowlist === undefined ? '' : 'Partial export - '}${effectiveDocumentReference?.title ?? 'Document'} - Zus ${format(new Date(), 'dd-MM-yyyy')}.pdf`;
            a.click();

            setTimeout(() => {
              URL.revokeObjectURL(pdfUrl);
            }, 200);
          }
        };

        if (patient?.id !== undefined && effectiveDocumentReference?.id !== undefined) {
          const apiBody = {
            patientId: patient.id,
            documentReferenceId: effectiveDocumentReference.id,
            sectionIds: cdaDocumentSectionIdAllowlist,
          };

          void fetch(`${getBffApiBaseUrl(requestContext.env)}/v1/cda/pdf?`, {
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(apiBody),
          })
            .then(async (response) => {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);

              performExportAction(url);
            })
            .finally(() => setControlMenuIsLoading(false));
        }
      },
      [
        externalDocumentReference,
        documentReference,
        patient,
        requestContext.authToken,
        requestContext.env,
      ],
    );

    // Set document XML when it finished loading (relevant for both client and server-side parsing)
    useEffect(() => {
      if (binaryData !== undefined) {
        setCdaDocumentXml(binaryData.binary.data ?? '');
      }
    }, [binaryData]);

    useEffect(() => {
      const effectiveDocumentReferenceId = externalDocumentReference?.id ?? documentReference?.id;
      if (
        isXmlDocument &&
        effectiveDocumentReferenceId !== undefined &&
        buildingCdaDocumentRef.current === false
      ) {
        buildingCdaDocumentRef.current = true;

        setCdaDocument(null);
        setCdaParseError(null);

        void fetch(`${getBffApiBaseUrl(requestContext.env)}/v1/cda/parsed`, {
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          method: 'POST',
          body: JSON.stringify({
            documentReferenceId: effectiveDocumentReferenceId,
          }),
        })
          .then(async (response) => {
            const responseJson = await response.json();

            let resolvedCdaDocument: CdaDocument;
            if (!(response.ok && responseJson?.data.attributes)) {
              resolvedCdaDocument = {
                documentIsExcessivelyLarge: false,
                id: effectiveDocumentReferenceId,
                partial: true,
                onlyNarratives: false,
                parseError: {
                  code: 'fetch-failure',
                  message: 'Failed to parse CDA document server-side',
                },
                detailFields: [],
                detailSubsections: [],
                searchIndexes: [],
                timings: {
                  normalizationDurationMilliseconds: 0,
                  parseToJsonDurationMilliseconds: 0,
                  parseSectionsDurationMilliseconds: 0,
                  mapToDocumentSchemaDurationMilliseconds: 0,
                  assembleDetailFieldsMilliseconds: 0,
                  searchIndexingMilliseconds: 0,
                  totalMilliseconds: 0,
                },
              };
            } else {
              resolvedCdaDocument = responseJson.data.attributes;
            }
            setCdaDocument(resolvedCdaDocument);

            const documentErrorCode = classifyCdaDocumentError(resolvedCdaDocument);
            if (documentErrorCode === 'no-content') {
              telemetry.logger.info('CDA document has no renderable content', {
                documentErrorCode,
                documentReferenceId: effectiveDocumentReferenceId,
              });
            } else if (documentErrorCode !== undefined) {
              telemetry.trackError({
                message: 'CDA document could not be displayed',
                error: new Error(`CDA document error: ${documentErrorCode}`),
                context: { documentErrorCode, documentReferenceId: effectiveDocumentReferenceId },
              });
            }
          })
          .catch((error) => {
            setCdaParseError(error instanceof Error ? error : new Error(String(error)));
            telemetry.trackError({
              error,
              message: 'Error parsing CDA document',
              // A network/parse throw means the document could not be fetched.
              context: {
                documentErrorCode: 'fetch-failure',
                documentReferenceId: effectiveDocumentReferenceId,
              },
            });
          })
          .finally(() => {
            buildingCdaDocumentRef.current = false;
          });
      }
    }, [
      externalDocumentReference?.id,
      documentReference?.id,
      isXmlDocument,
      requestContext.env,
      requestContext.authToken,
      telemetry,
    ]);

    const documentTitle = useMemo(() => {
      if (binaryData?.binary.contentType === 'text/plain') {
        return 'Plain Text Document';
      }

      return cdaDocument?.metadata?.document?.title ?? documentReference?.title ?? 'Unknown';
    }, [binaryData, cdaDocument, documentReference]);

    const documentDetailFields = useMemo(() => {
      if (cdaDocument) {
        return cdaDocument.detailFields;
      }

      if (documentReference) {
        return compact([
          documentReference.dateCreated
            ? {
                label: 'Date',
                value: documentReference.dateCreated,
                promoted: true,
                rank: 1,
              }
            : undefined,
          documentReference.subject
            ? { label: 'Patient Name', value: documentReference.subject, promoted: true, rank: 2 }
            : undefined,
          documentReference.authors && documentReference.authors.length > 0
            ? {
                label: 'Author',
                value: documentReference.authors.join(', '),
                promoted: true,
                rank: 3,
              }
            : undefined,
        ]);
      }

      return [];
    }, [cdaDocument, documentReference]);

    if (isLoading) {
      return (
        <article
          className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden`}
        >
          <LoadingSpinner
            message="Loading document..."
            centered={true}
            className={tw`min-h-[20vh]`}
          />
        </article>
      );
    }

    /*
     * To render a document we need:
     * 1. A Patient – for displaying patient metadata
     * 2. A Binary – for parsing and rendering the document content
     * 3. A DocumentReference — for document metadata such as the document title
     *
     * The astute reviewer might reason that a DocumentReference is not strictly required for CDA
     * documents since all metadata also exists in the parsed binary, and that other document types
     * could be rendered without one as well with the caveat that they would be missing a document
     * title and metadata. Right now our implementation requires one for all document types, a
     * constraint we will likely relax in the future (https://zeushealth.atlassian.net/browse/APP-5003).
     */
    if (
      !(patient && binaryData?.binary && documentReference) ||
      (isXmlDocument && cdaParseError !== null)
    ) {
      return (
        <article
          className={tw`flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden p-8`}
        >
          <ContentError title="Unable to load this document">
            <span>If this problem persists </span>
            <SupportFormLink buttonText="contact support" />.
          </ContentError>
        </article>
      );
    }

    return (
      <ErrorBoundary boundaryName="DocumentViewer" initialContext={boundaryContext}>
        {() => (
          <DocumentViewerProvider controls={controls}>
            <CdaSectionSelectionProvider
              sectionIds={cdaDocument?.content?.sections?.map((section) => section.id) ?? []}
            >
              <div
                ref={headerContainerRef}
                className={tw`@container/header flex h-full max-h-full min-h-full w-full max-w-full flex-col overflow-hidden bg-white`}
              >
                <div
                  className={tw`flex w-full min-w-full max-w-full flex-wrap items-center justify-between gap-2 overflow-visible bg-content-main px-5 pt-4`}
                >
                  <div
                    className={tw`flex w-full min-w-full max-w-full @4xl/header:flex-row flex-col gap-2`}
                  >
                    <div className={tw`flex flex-1 items-start gap-2`}>
                      {controls.close && (
                        <DocumentViewerControlButton
                          label={screenBreakpoints.isAtLeast.lg ? 'Close' : 'Back'}
                          testId={
                            screenBreakpoints.isAtLeast.lg
                              ? DOCUMENT_VIEWER_CONTROL_BUTTON_CLOSE_TEST_ID
                              : DOCUMENT_VIEWER_CONTROL_BUTTON_BACK_TEST_ID
                          }
                          icon={screenBreakpoints.isAtLeast.lg ? faXmark : faArrowLeft}
                          telemetryInteractionName="document_viewer_control_click_close"
                          onClick={() => onClose?.()}
                        />
                      )}
                      <Heading
                        level="h1"
                        className={tw`line-clamp-2 font-medium text-white`}
                        deepLink={{
                          url: `${window.location.origin}${pathWithParameters(
                            window.location.href,
                            {
                              documentSectionId: null,
                            },
                            ['builderId', 'documentId'],
                          )}`,
                          onSuccessToast: {
                            type: 'success',
                            title: 'Copied Document Link',
                            body: 'A shareable link to this document has been copied to your clipboard.',
                          },
                        }}
                      >
                        {documentTitle}
                      </Heading>
                    </div>
                    <div
                      className={tw`flex h-full w-full @4xl/header:max-w-fit flex-1 items-center @4xl/header:justify-end justify-between gap-4`}
                    >
                      <div className={tw`flex flex-0 items-center gap-1`}>
                        {controls.openInNewTab && service === 'standalone' && (
                          <DocumentViewerControlLink
                            label="Open in new Tab"
                            icon={faArrowUpRightFromSquare}
                            testId={DOCUMENT_VIEWER_CONTROL_BUTTON_OPEN_IN_NEW_TAB_TEST_ID}
                            target="_blank"
                            href={`/patient/${patient.UPID}/document/${documentReference.id}`}
                            telemetryInteractionName="document_viewer_control_click_open_in_new_tab"
                          />
                        )}
                        {!isPlainTextDocument && controls.print && (
                          <DocumentViewerControlButton
                            label="Print"
                            icon={faPrint}
                            testId={DOCUMENT_VIEWER_CONTROL_BUTTON_PRINT_TEST_ID}
                            onClick={(event) => {
                              event.stopPropagation();
                              setControlMenuIsLoading(true);
                              if (isXmlDocument) {
                                exportPdf({ exportAction: 'print' });
                              } else {
                                printRaw(event);
                              }
                            }}
                            telemetryInteractionName="document_viewer_control_click_print"
                          />
                        )}
                        {controls.download &&
                          (isXmlDocument ? (
                            <DocumentViewerControlMenu
                              label="Download"
                              icon={faFileDownload}
                              testId={DOCUMENT_VIEWER_CONTROL_MENU_DOWNLOAD_TEST_ID}
                              loading={controlMenuIsLoading}
                              controls={[
                                {
                                  label: 'Download XML',
                                  testId: DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID,
                                  telemetryInteractionName:
                                    'document_viewer_control_click_download_raw',
                                  onClick: (event) => {
                                    setControlMenuIsLoading(true);
                                    downloadRaw(event);
                                  },
                                },
                                {
                                  label: 'Download PDF',
                                  testId: DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_PDF_TEST_ID,
                                  telemetryInteractionName:
                                    'document_viewer_control_click_download_pdf',
                                  onClick: (event) => {
                                    event.stopPropagation();
                                    setControlMenuIsLoading(true);
                                    exportPdf({ exportAction: 'download' });
                                  },
                                },
                              ]}
                            />
                          ) : (
                            <DocumentViewerControlButton
                              label="Download"
                              testId={DOCUMENT_VIEWER_CONTROL_BUTTON_DOWNLOAD_RAW_TEST_ID}
                              icon={faFileDownload}
                              onClick={downloadRaw}
                              telemetryInteractionName="document_viewer_control_click_download_raw"
                            />
                          ))}
                      </div>
                      {cdaDocument && isXmlDocument && (
                        <div
                          className={tw`flex @4xl/header:w-96 w-64 @4xl/header:min-w-96 min-w-[12rem] @4xl/header:max-w-96 max-w-64 items-center justify-end gap-1 @sm:max-xl/header:flex-1`}
                        >
                          <CdaSearchInput cdaDocument={cdaDocument} className={tw`w-full`} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className={twx(
                      'flex h-[68px] w-full flex-col justify-end pt-2 pb-0 opacity-100 transition-all duration-500',
                      {
                        'pointer-events-none h-0 opacity-0': collapsedHeader,
                        'pb-2': !collapsedHeader,
                      },
                    )}
                  >
                    <DocumentViewerHeaderFields
                      fields={documentDetailFields.filter((field) => field.promoted) ?? []}
                    />
                  </div>

                  {contentContainerWidth > 0 &&
                    (contentBreakpoints.isAtLeast.sm ? (
                      <div className={tw`pt-0`}>
                        <DocumentViewerTabs
                          tabs={tabs}
                          selectedTab={selectedTab}
                          onTabSelected={(id) =>
                            setSelectedTab(tabs.find((tab) => tab.id === id) ?? tabs[0])
                          }
                        />
                      </div>
                    ) : (
                      <div className={tw`flex w-full items-center justify-center px-4 pb-4`}>
                        <DocumentViewerTabMenu
                          tabs={tabs}
                          selectedTab={selectedTab}
                          onTabSelected={(id) =>
                            setSelectedTab(tabs.find((tab) => tab.id === id) ?? tabs[0])
                          }
                        />
                      </div>
                    ))}
                </div>
                <div
                  ref={contentContainerRef}
                  className={tw`flex h-full max-h-full w-full flex-1 overflow-hidden`}
                >
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'document',
                    })}
                  >
                    <DocumentRenderer
                      key={`${documentReference.id}-${binaryData?.binary?.id}-${cdaDocument?.id}`}
                      controls={controls}
                      cdaDocument={cdaDocument}
                      document={documentReference}
                      binaryData={binaryData}
                      contentRef={contentRef}
                    />
                    {isXmlDocument && cdaDocument && (
                      <CdaDocumentSelectionContextBar
                        cdaDocument={cdaDocument}
                        downloadPdf={exportPdf}
                      />
                    )}
                  </div>
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'details',
                    })}
                  >
                    <div className={tw`flex flex-col gap-5 p-5`}>
                      <div>
                        <Heading level="h3">Document Details</Heading>
                        <p className={tw`text-content-subtle text-sm italic`}>
                          This section contains information from when the document was originally
                          created.
                        </p>
                      </div>
                      {documentDetailFields.length > 0 && (
                        <DocumentViewerDetailFields fields={documentDetailFields} />
                      )}
                      {orderBy(cdaDocument?.detailSubsections ?? [], 'rank').map((subsection) => (
                        <DocumentViewerDetailSubsection
                          key={subsection.title}
                          subsection={subsection}
                        />
                      ))}
                    </div>
                  </div>
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'xml',
                    })}
                  >
                    <pre
                      className={tw`scroll-shadows h-full w-full overflow-auto whitespace-pre-wrap p-5 font-mono`}
                    >
                      {prettifiedCdaDocumentXml}
                    </pre>
                  </div>
                  <div
                    className={twx('hidden', {
                      contents: selectedTab.id === 'pdf_preview',
                    })}
                  >
                    {cdaDocument && selectedTab.id === 'pdf_preview' && (
                      <ErrorBoundary
                        boundaryName="Document Viewer PDF Preview"
                        errorMessage={() => 'Unable to load PDF preview'}
                      >
                        {() =>
                          cdaDocumentPdfPreviewUrl ? (
                            <iframe
                              title="PDF Preview"
                              src={cdaDocumentPdfPreviewUrl}
                              className={tw`pdf-document h-full w-full rounded-md`}
                            />
                          ) : (
                            <LoadingSpinner centered={true} message="Loading PDF preview..." />
                          )
                        }
                      </ErrorBoundary>
                    )}
                  </div>
                </div>
              </div>
            </CdaSectionSelectionProvider>
          </DocumentViewerProvider>
        )}
      </ErrorBoundary>
    );
  },
  (prevProps, nextProps) =>
    prevProps.binaryId === nextProps.binaryId &&
    prevProps.documentReference?.id === nextProps.documentReference?.id &&
    prevProps.binaryData?.binary?.id === nextProps.binaryData?.binary?.id,
);

interface CdaDocumentSelectionContextBarProps {
  cdaDocument: CdaDocument;
  downloadPdf: (args: ExportPdfArgs) => void;
}

const CdaDocumentSelectionContextBar = ({
  cdaDocument,
  downloadPdf,
}: CdaDocumentSelectionContextBarProps) => {
  const { selectedSectionIds, clearSelectedSectionIds, selectAllSectionIds } =
    useCdaSectionSelectionContext();

  const [downloadButtonSectionCount, setDownloadButtonSectionCount] = useState(
    selectedSectionIds.length,
  );

  useEffect(() => {
    // When going from N->0 selected sections (which implicitly hides the selection context bar),
    // defer the update to the selection count so that as the context bar fades out it still shows
    // the previously selected count instead of flashing to `0` while it animates out
    if (selectedSectionIds.length === 0) {
      setTimeout(() => setDownloadButtonSectionCount(selectedSectionIds.length), 300);
    } else {
      setDownloadButtonSectionCount(selectedSectionIds.length);
    }
  }, [selectedSectionIds]);

  return (
    <aside
      data-testid={DOCUMENT_VIEWER_SELECTION_CONTEXT_BAR_TEST_ID}
      className={twx(
        'fixed right-0 left-0 mx-auto h-[52px] max-h-[52px] min-h-[52px] min-w-[240px] max-w-[680px] transition-all duration-300',
        {
          'bottom-[32px] opacity-100': selectedSectionIds.length > 0,
          'pointer-events-none -bottom-[0px] opacity-0': selectedSectionIds.length === 0,
        },
      )}
    >
      <div
        className={tw`mx-[32px] flex h-full w-full items-center rounded-md border border-border-main bg-white/90 px-2 py-3 shadow-2xl backdrop-blur-[2px]`}
      >
        <div className={tw`flex h-full min-h-full items-center`}>
          <Button
            type="button"
            ariaLabel="Clear Selected Sections"
            variant="unstyled"
            size="sm"
            className={tw`flex h-[30px] max-h-[30px] min-h-[30px] w-[30px] min-w-[30px] max-w-[30px] items-center justify-center rounded-full bg-white p-2 text-content-main shadow-none transition-all duration-300 hover:bg-content-subtle/10 active:scale-[.98] active:bg-content-subtle/15`}
            telemetryInteractionName="document_viewer_deselect_all_sections"
            telemetryContext={{
              documentReferenceId: cdaDocument.id,
              sectionCount: selectedSectionIds.length,
            }}
            onClick={clearSelectedSectionIds}
          >
            <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
          </Button>
          <VerticalDivider className={tw`mx-1 border-border-main`} />
          <Button
            type="button"
            ariaLabel="Select All"
            variant="link"
            size="sm"
            className={tw`flex items-center justify-center bg-white p-2 text-content-main text-sm leading-tight shadow-none transition-all duration-300 hover:bg-content-subtle/10 hover:no-underline active:scale-[.98] active:bg-content-subtle/15`}
            telemetryInteractionName="document_viewer_select_all_sections"
            telemetryContext={{
              documentReferenceId: cdaDocument.id,
              sectionCount: cdaDocument.content?.sections.length ?? 0,
            }}
            onClick={selectAllSectionIds}
          >
            <span>Select All</span>
          </Button>
        </div>
        <div className={tw`flex flex-1 items-center justify-end`}>
          <Button
            type="button"
            ariaLabel="Download Selected sections as PDF"
            variant="primary"
            size="sm"
            className={tw`flex items-center gap-1`}
            telemetryInteractionName="document_viewer_download_selected_sections"
            telemetryContext={{
              documentReferenceId: cdaDocument.id,
              sectionCount: selectedSectionIds.length,
            }}
            onClick={() =>
              downloadPdf({
                exportAction: 'download',
                cdaDocumentSectionIdAllowlist: selectedSectionIds,
              })
            }
          >
            <FontAwesomeIcon icon={faFileDownload} className={tw`h-4 w-4`} />
            <span className={tw`font-medium text-sm`}>
              Download {downloadButtonSectionCount}&nbsp;
              {downloadButtonSectionCount <= 1 ? 'Section' : 'Sections'}
            </span>
          </Button>
        </div>
      </div>
    </aside>
  );
};
